/**
 * The contract the ordering screens read against. The API does not serve these
 * yet — menus, schedules and orders land in P3/P4 — but the design has settled
 * their shape, so the types live here rather than inside one app.
 */
export const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
export const MEAL_LABELS = {
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
};
export const ORDER_STATUSES = [
    'placed',
    'confirmed',
    'delivered',
    'cancelled',
    'cancelled_exception',
];
/** True while the slot still accepts orders. Compared against server time. */
export function isOpen(cutoffAt, now = new Date()) {
    return new Date(cutoffAt).getTime() > now.getTime();
}
/** `47:12` — minutes and seconds under an hour, `H:MM:SS` beyond it. */
export function formatCountdown(cutoffAt, now = new Date()) {
    const ms = new Date(cutoffAt).getTime() - now.getTime();
    if (ms <= 0)
        return '0:00';
    const total = Math.floor(ms / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
/**
 * How long is left, in words.
 *
 * Never a clock face. "19:49:28" is read as a time of day — twenty to eight in
 * the evening — not as nineteen hours, which is the one thing it has to say.
 * Seconds appear only in the last minute, where they are the whole point.
 */
export function formatTimeLeft(cutoffAt, now = new Date()) {
    const ms = new Date(cutoffAt).getTime() - now.getTime();
    if (ms <= 0)
        return 'closed';
    const minutes = Math.floor(ms / 60_000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days >= 2)
        return `${days} days`;
    if (hours >= 24)
        return `${days} day ${hours % 24}h`;
    if (hours >= 1)
        return `${hours}h ${minutes % 60}m`;
    if (minutes >= 1)
        return `${minutes} min`;
    return `${Math.floor(ms / 1000)} sec`;
}
/** The same length, short enough to sit inside the ring: "3d", "19h", "6m". */
export function formatTimeLeftShort(cutoffAt, now = new Date()) {
    const ms = new Date(cutoffAt).getTime() - now.getTime();
    if (ms <= 0)
        return '0m';
    const minutes = Math.floor(ms / 60_000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days >= 1)
        return `${days}d`;
    if (hours >= 1)
        return `${hours}h`;
    if (minutes >= 1)
        return `${minutes}m`;
    return `${Math.floor(ms / 1000)}s`;
}
/**
 * When it shuts, said the way somebody would say it out loud.
 *
 * The hour alone is ambiguous the moment the cutoff is not today, and a cutoff
 * the evening before is the normal case rather than the exception.
 */
export function formatCutoffWhen(cutoffAt, now = new Date()) {
    const cutoff = new Date(cutoffAt);
    const time = cutoff.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const day = (d) => d.toLocaleDateString('en-CA');
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (day(cutoff) === day(now))
        return `today ${time}`;
    if (day(cutoff) === day(tomorrow))
        return `tomorrow ${time}`;
    return `${cutoff.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`;
}
/**
 * A service date as a person says it: "Fri, Sep 18".
 *
 * Asking Intl for a weekday and a day number without a month gets "18 Fri" in
 * en-US, which reads as a date nobody writes. Naming the month is both clearer
 * and the only way to get the parts in the order people expect.
 */
export function formatServiceDay(serviceDate, now = new Date()) {
    const date = new Date(`${serviceDate}T00:00:00`);
    const day = (d) => d.toLocaleDateString('en-CA');
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (serviceDate === day(now))
        return 'today';
    if (serviceDate === day(tomorrow))
        return 'tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
export function formatMoney(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
}
/** Storage prefix for a menu photo, so the upload path matches the entity. */
export const MENU_PHOTO_ENTITY = 'menu';
/* ------------------------------------------------------------------ *
 * Organisation — sites, departments, delivery points, restaurants     *
 * ------------------------------------------------------------------ */
export const SITE_KINDS = ['factory', 'office'];
export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** True when the kitchen is shut on that date. */
export function isClosedOn(restaurant, date) {
    const iso = date.getDay() === 0 ? 7 : date.getDay();
    return restaurant.closedWeekdays.includes(iso);
}
/** How a cutoff rule reads to a human. */
export function describeCutoff(rule) {
    if (rule.offsetDays === 0)
        return `same day ${rule.cutoffTime}`;
    if (rule.offsetDays === 1)
        return `day before ${rule.cutoffTime}`;
    return `${rule.offsetDays} days before, ${rule.cutoffTime}`;
}
const CANCELLED_STATUSES = new Set(['cancelled', 'cancelled_exception']);
/**
 * Whether the person who placed an order may still take it back.
 *
 * Only a placed order before its cutoff. At the cutoff the sweep confirms it
 * and the kitchen starts cooking; a cancelled one has nothing left to cancel.
 * The server decides for real — this only decides whether to offer the
 * button, so a screen never shows an action that is certain to be refused.
 */
export function isCancellable(order, now = new Date()) {
    return order.status === 'placed' && new Date(order.editableUntil).getTime() > now.getTime();
}
/**
 * One order per meal: the one that stands.
 *
 * Changing an order cancels the old one and places a new one, so a lunch
 * changed twice is three rows, two of them "cancelled" for a meal that was in
 * fact eaten. History should say what happened to each meal, not every step
 * of getting there.
 *
 * The live order wins wherever there is one — at most one per meal is ever
 * live. Where every order for a meal was cancelled, the first one listed is
 * kept, which from `/api/orders/mine` is the newest. Each meal stays where it
 * first appeared, so a newest-first list stays newest first.
 */
export function latestPerMeal(orders) {
    const chosen = new Map();
    for (const order of orders) {
        const key = `${order.serviceDate}|${order.mealType}`;
        const current = chosen.get(key);
        if (!current) {
            chosen.set(key, order);
        }
        else if (CANCELLED_STATUSES.has(current.status) && !CANCELLED_STATUSES.has(order.status)) {
            chosen.set(key, order);
        }
    }
    // A Map keeps its keys in the order they were first set.
    return [...chosen.values()];
}
//# sourceMappingURL=ordering.js.map